import './app.css';

import DayjsUtils from '@date-io/dayjs';
import { CssBaseline, ThemeProvider } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ColorThemeWrapper, darkTheme, SnackbarProvider } from '@nutrien/cxp-components';
import { requireDefined } from '@nutrien/ddc-fe-util';
import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { Provider as RXDBProvider } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import { useRxDB } from '../../rxdb/useRxDB';
import type { FeatureFlags } from '../../types/featureFlags';
import { useAIMSHeartbeat } from '../../utilities/hooks/useAIMSHeartbeat';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useFeatureFlags } from '../../utilities/useFeatureFlag';
import useInterval from '../../utilities/useInterval';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import AIMSHeartbeat from '../AIMSHeartbeat';
import AppBarController from '../AppBarController';
import CacheUpdateHandler from '../CacheUpdateHandler';
import Checklist from '../Checklist';
import CompleteInspection from '../CompleteInspection';
import DataLoadingDialog from '../DataLoadingDialog';
import Deficiencies from '../Deficiencies';
import Home from '../Home';
import InspectionDetails from '../InspectionDetails';
import InspectionList from '../InspectionList';
import Loading from '../Loading';
import Login from '../Login';
import ForgotPassword from '../LoginFlow/ForgotPassword';
import Logout from '../Logout';
import PMChecklist from '../PMChecklist';
import PWAUpdate from '../PWAUpdate';
import { triggerPWAUpdate } from '../PWAUpdate/PWAUpdate';
import ReloadHandler from '../ReloadHandler';
import SignUp from '../SignUp';
import TabBar from '../TabBar';
import WorkPackages from '../WorkPackages';

const env = requireDefined(import.meta.env.VITE_REACT_APP_ENV);
const App: React.FC = () => {
  const { db } = useRxDB();
  const { HandleGesture, configCatFlag, appVersion } = useMst();
  const { sendAIMSHeartbeat } = useAIMSHeartbeat();

  const online = useOnlineStatus();
  const { location } = window;
  const featureFlag: FeatureFlags = useFeatureFlags();

  useEffect(() => {
    try {
      if (featureFlag) {
        configCatFlag.setRedBanner(
          String(featureFlag?.outageMessage ?? ''),
          featureFlag?.outageIsActive as boolean,
        );
      }
    } catch (error) {
      sentryCaptureException(error, '~ file: App.tsx ~ Outage Red Banner ~ featureFlagUseEffect ~');
    }

    try {
      if (featureFlag) {
        configCatFlag.setBlueBanner(
          String(featureFlag?.outageWarningMessage ?? ''),
          featureFlag?.outageWarningIsActive as boolean,
        );
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: App.tsx ~ Outage Blue Banner ~ featureFlagUseEffect ~',
      );
    }
  }, [featureFlag, configCatFlag]);

  useEffect(() => {
    const checkIfAuthenticated = async () => {
      try {
        const currentSession = await Auth.currentSession();
        // Setup user in sentry
        if (currentSession.getIdToken().decodePayload().email) {
          const { email } = currentSession.getIdToken().decodePayload();
          Sentry.setUser({ email });
        }
        if (!currentSession.isValid() && location.pathname.indexOf('login') === -1) {
          setTimeout(() => {
            location.href = '/login';
          }, 3000);
        }
      } catch (error) {
        console.error('🚀 ~ file: App.tsx ~ line 79 ~ checkIfAuthenticated ~ err', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(error, `~ file: App.tsx ~ line 79 ~ checkIfAuthenticated ${error}`);
        if (location.pathname.indexOf('login') === -1) {
          setTimeout(() => {
            location.href = '/login';
          }, 3000);
        }
      }
    };

    if (location.pathname.indexOf('logout') === -1) checkIfAuthenticated();
  }, []);

  const handleSuccessfulLogin = async () => {
    try {
      const currentSession = await Auth.currentSession();
      if (currentSession.isValid() && location.pathname.indexOf('home') === -1) {
        await sendAIMSHeartbeat({ loginTime: dayjs() });
        location.href = '/home';
      }
    } catch (error) {
      console.log(error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: App.tsx ~ line 110 ~ handleSuccessfulLogin ~');
    }
  };

  const onNewServiceWorkerUpdate = (newVersion: string) => {
    appVersion.setNewVersionAvailable(newVersion);
    if (env !== 'local') {
      triggerPWAUpdate();
    }
  };

  // Refresh tokens on 20min interval
  useInterval(async () => {
    if (db && online) {
      db?.refreshTokensOnAllCollections();
    }
  }, 1 * 20 * 60 * 1000);

  return (
    <RXDBProvider db={db?.db}>
      <MuiPickersUtilsProvider utils={DayjsUtils}>
        <ThemeProvider theme={darkTheme}>
          <ColorThemeWrapper darkmode>
            <CssBaseline />
            <CacheUpdateHandler />
            <ReloadHandler />
            <SnackbarProvider
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              bottom="76px"
            >
              {HandleGesture.isCompleted && <DataLoadingDialog />}
              <PWAUpdate
                serviceWorkerFile="service-worker.js"
                onNewServiceWorkerUpdate={onNewServiceWorkerUpdate}
              />
              <Router>
                <AIMSHeartbeat />
                <AppBarController />
                <Switch>
                  <Route path="/initializing" exact>
                    <Loading loginHandler={handleSuccessfulLogin} />
                  </Route>
                  <Route path="/home" exact>
                    <Home />
                  </Route>
                  <Route path="/work-packages" exact>
                    <WorkPackages />
                  </Route>
                  <Route path="/inspection-list" exact>
                    <InspectionList />
                  </Route>
                  <Route path="/inspection-details" exact>
                    <InspectionDetails />
                  </Route>
                  <Route path="/deficiencies" exact>
                    <Deficiencies />
                  </Route>
                  <Route path="/checklist" exact>
                    <Checklist />
                  </Route>
                  <Route path="/PMChecklist" exact>
                    <PMChecklist />
                  </Route>
                  <Route path="/complete-inspection" exact>
                    <CompleteInspection />
                  </Route>
                  <Route path="/login" exact>
                    <Login />
                  </Route>
                  <Route path="/forgot-password" exact>
                    <ForgotPassword />
                  </Route>
                  <Route path="/signup" exact>
                    <SignUp />
                  </Route>
                  <Route path="/logout" exact>
                    <Logout rxdb={db.db} />
                  </Route>
                  <Route path="/">
                    <Redirect to="/initializing" />
                  </Route>
                </Switch>
                <TabBar />
              </Router>
            </SnackbarProvider>
          </ColorThemeWrapper>
        </ThemeProvider>
      </MuiPickersUtilsProvider>
    </RXDBProvider>
  );
};

export default observer(App);
